import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import AddButton from "../../../../../components/AddButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import EditButton from "../../../../../components/EditButton";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";
import { toast } from "../../../../../components/toastNotification";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";
import useDeleteOnlineDirectories from "../hooks/useDeleteOnlineDirectories";
import useGetOnlineDirectoriesList from "../hooks/useGetOnlineDirectories";
import {
  addStateOnlineDirectories,
  onlineDirectoriesFetching,
  onlineDirectoriesFilterText,
  onlineDirectoriesReFetching,
  editStateOnlineDirectories,
  rowEditOnlineDirectories,
  selectedRowsStateOnlineDirectories,
} from "../atoms/atoms";
import { IOnlineDirectories, IOnlineDirectoriesTableData } from "../types";
import Timer from "components/Timer";
import TableFilters from "components/TableFilters";
import OnlineDirectoriesForm from "../form/OnlineDirectoryForm";

const OnlineDirectoriesTable = () => {
  const { data: directData, refetch: reFetch } = useGetOnlineDirectoriesList();

  const BulkDelete = useDeleteOnlineDirectories();

  //Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(
    addStateOnlineDirectories
  );
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateOnlineDirectories
  );
  const [actionEditState, setActionEditState] = useRecoilState(
    editStateOnlineDirectories
  );
  const [filterTextState, setFilterTextState] = useRecoilState(
    onlineDirectoriesFilterText
  );
  const setEditRowData = useSetRecoilState(rowEditOnlineDirectories);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [isFetching, setIsFetching] = useRecoilState(onlineDirectoriesFetching);
  const isReFetching = useRecoilValue(onlineDirectoriesReFetching);

  //Local States
  const [tableData, setTableData] = useState<IOnlineDirectories[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState<
    IOnlineDirectories[]
  >([]);

  // Permission States
  const addPermission =
    PERMISSIONS.permission_utilities_digitalassets_onlinedirectories_add;
  const editPermission =
    PERMISSIONS.permission_utilities_digitalassets_onlinedirectories_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_digitalassets_onlinedirectories_delete;

  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };

  // Get Data
  useEffect(() => {
    reFetch();
  }, [isReFetching]);

  useEffect(() => {
    if (directData?.status === 400) {
      toast.fire("Server Error", directData?.data.Message, "error");
      setTableData([]);
      setIsFetching(false);
    } else {
      Loader();
    }
  }, [directData]);

  // For Recent Activity + Search
  let location = useLocation();
  let recordIdFromRecent;

  const [recent, setRecent] = useState("");
  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      notificationFilterById(
        recordIdFromRecent.recordId,
        recordIdFromRecent.moduleName,
        recordIdFromRecent.subModuleName
      ).then((result: IOnlineDirectories[] | undefined) => {
        if (typeof result !== "object") {
          toast.fire("Failed", result, "error");
        } else {
          setTableFilteredData(result);
          setRecent(result[0].brandName);
        }
      });
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
  }, [recordIdFromRecent]);

  //For Filter/Search
  const filtereditems =
    tableData &&
    tableData?.filter(
      (item) =>
        item.brandName &&
        item.brandName
          .toLowerCase()
          .includes(filterTextState?.trim().toLowerCase())
    );
  useEffect(() => {
    if (!recent) {
      setTableFilteredData(filtereditems);
    }
  }, [filterTextState]);

  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows: IOnlineDirectoriesTableData[]) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData: IOnlineDirectoriesTableData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    setIsFetching(true);
    const ids =
      selectedRows && selectedRows.map((row) => row.onlineDirectoryId);
    BulkDelete(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Brands" : "Brand"
    }?`}</div>
  );

  const matColumns = [
    {
      title: "Brand Name",
      field: "brandName",
      customSort: (a, b) =>
        a.brandName && b.brandName
          ? a.brandName.trim().localeCompare(b.brandName.trim())
          : a.brandName
          ? 0
          : -1,
    },
    {
      title: "Description",
      field: "description",
      customSort: (a, b) =>
        a.description && b.description
          ? a.description.trim().localeCompare(b.description.trim())
          : a.description
          ? 0
          : -1,
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      sorting: false,
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white p-4">
      {actionAddState || actionEditState ? (
        <OnlineDirectoriesForm setRecent={setRecent} />
      ) : (
        <>
          <div className="flex justify-between text-xs items-center w-full">
            <TableFilters
              defaultText={filterTextState}
              textInputName={"OnlineDirectoriesName"}
              columnDetails={"Brand Name"}
              filterText={setFilterTextState}
              recentText={recent}
              setRecent={setRecent}
              disabledButton={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              disabledTextBox={
                selectedRows !== undefined && selectedRows.length > 0
              }
            />

            <div className="">
              {userPermissions.includes(addPermission) ? (
                <AddButton
                  buttonText="New Online Directory"
                  onClick={() => handleAdd()}
                  disabled={
                    actionEditState ||
                    (selectedRows !== undefined && selectedRows.length > 0)
                  }
                />
              ) : (
                ""
              )}
            </div>
          </div>

          <div>
            {userPermissions.includes(deletePermission) ? (
              <div className="text-secondary italic text-xs3 my-2">
                <p className="font-bold italic inline-block mr-1">Note:</p>
                <span>Tick the box to deactivate some data.</span>
              </div>
            ) : (
              <div className="mb-4"></div>
            )}
          </div>

          <div className="">
            <MaterialTableTS
              data={
                isFetching
                  ? []
                  : filterTextState === "" && recent === ""
                  ? tableData
                  : filterTextState === ""
                  ? tableFilteredData
                  : filtereditems
              }
              showTitle={true}
              columns={matColumns}
              isLoading={isFetching}
              onSelectionChange={handleSelectedRowsChange}
              selectedRows={selectedRows}
              selection={
                !actionEditState &&
                !actionAddState &&
                userPermissions.includes(deletePermission)
              }
              setConfirmModalOpen={setOpenModalDelete}
            />
          </div>

          <ConfirmBoxDelete
            ModalTitle="Deactivate Brand"
            qConfirmation={deleteMessage}
            isOpen={openModalDelete}
            onClose={() => {
              setOpenModalDelete(false);
            }}
            onClickFunction={handleDelete}
            cancelButtonRef={undefined}
          />
        </>
      )}
    </div>
  );
};

export default OnlineDirectoriesTable;
